import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Table,
  Button,
  SpaceBetween,
  Modal,
  FormField,
  Input,
  Box,
  Flashbar,
  Spinner,
} from '@amzn/awsui-components-react';
import { useGetSessionsQuery, useCreateSessionMutation, useDeleteSessionMutation } from 'src/store/apis/uatApi';
import { UatSessionSummary } from 'src/store/interfaces/Uat.types';
import { getUserId } from 'src/auth/auth';
import { canDeleteSession } from 'src/components/Uat/canDeleteSession';
import useNotification from 'src/hooks/useNotification';
import { usePermissions } from 'src/hooks/usePermissions';
import {
  SelectedOption,
  LLMTypeSelect,
  PhaseSelect,
  ToolSelectionTable,
  useToolsByPhase,
  uatLlmTypeOptions,
} from 'src/components/common/ExecutionScopeFields';

const UatSessions: FC = () => {
  const navigate = useNavigate();
  const { addNotification, notificationItems } = useNotification();
  const { canAccessUat, isAdmin } = usePermissions();
  // Same alias the service derives from the token (identities.userId); Redux's AuthUser.username is the Cognito one.
  const currentUser = getUserId();
  const { data: sessions, isLoading, refetch: refetchSessions } = useGetSessionsQuery();
  const [createSession, { isLoading: isCreating }] = useCreateSessionMutation();
  const [deleteSession, { isLoading: isDeleting }] = useDeleteSessionMutation();
  const { toolsByPhase, isLoading: isLoadingTools } = useToolsByPhase();

  const [showModal, setShowModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<UatSessionSummary | null>(null);
  const [sessionName, setSessionName] = useState('');
  const [selectedLLMType, setSelectedLLMType] = useState<SelectedOption | null>(uatLlmTypeOptions[0]);
  const [selectedPhase, setSelectedPhase] = useState<SelectedOption | null>(null);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);

  const resetForm = () => {
    setSessionName('');
    setSelectedLLMType(uatLlmTypeOptions[0]);
    setSelectedPhase(null);
    setSelectedToolIds([]);
  };

  const handlePhaseChange = (option: SelectedOption) => {
    setSelectedPhase(option);
    setSelectedToolIds([]);
  };

  const handleCreate = async () => {
    if (!selectedLLMType || !selectedPhase) return;

    // Build toolVersionRevision from selected tools
    const phase = selectedPhase.value;
    const tools = toolsByPhase?.[phase] ?? [];
    const toolVersionRevision: Record<string, { version: string; revision: number; status: string | null }> = {};
    selectedToolIds.forEach((id) => {
      const t = tools.find((tool) => tool.toolId === id);
      if (t) toolVersionRevision[id] = { version: t.version, revision: t.revision, status: t.status ?? null };
    });

    try {
      const result = await createSession({
        sessionName,
        executionScope: {
          clientLLMType: selectedLLMType.value,
          phase,
          enabledTools: selectedToolIds,
          toolVersionRevision,
        },
      }).unwrap();
      setShowModal(false);
      resetForm();
      navigate(`/uat/sessions/${result.sessionId}`);
    } catch {
      addNotification('error', 'create-error', 'Failed to create session.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteSession({ sessionId: pendingDelete.sessionId }).unwrap();
      setPendingDelete(null);
    } catch (err) {
      // The 409 body already names the other participants — showing a generic string
      // throws away the only information that tells the user why it refused.
      const status = (err as { status?: number })?.status;
      const msg =
        (err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete session.';
      addNotification('error', 'delete-error', msg);
      // A rejected mutation invalidates nothing, so a 404 (someone else already deleted it)
      // would otherwise leave the row on screen contradicting the error.
      if (status === 404) refetchSessions();
      setPendingDelete(null);
    }
  };

  const isFormValid = sessionName.trim() && selectedLLMType && selectedPhase && selectedToolIds.length > 0;

  return (
    <ContentLayout header={<Header variant="h1">UAT Sessions</Header>}>
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} />
        {isLoading ? (
          <Spinner size="large" />
        ) : (
          <Table
            header={
              <Header
                variant="h2"
                actions={canAccessUat && <Button variant="primary" onClick={() => setShowModal(true)}>Create Session</Button>}
              >
                Sessions
              </Header>
            }
            items={sessions ?? []}
            empty={<Box textAlign="center" padding="l">No sessions yet.</Box>}
            columnDefinitions={[
              {
                id: 'sessionName',
                header: 'Session Name',
                cell: (item) => (
                  <Button variant="link" onClick={() => navigate(`/uat/sessions/${item.sessionId}`)}>
                    {item.sessionName}
                  </Button>
                ),
              },
              { id: 'createdBy', header: 'Created By', cell: (item) => item.createdBy },
              { id: 'createdTs', header: 'Created', cell: (item) => new Date(item.createdTs).toLocaleString() },
              { id: 'participants', header: 'Participants', cell: (item) => item.participants.join(', ') },
              { id: 'totalQueries', header: 'Queries', cell: (item) => item.totalQueries },
              { id: 'totalFeedbacks', header: 'Feedbacks', cell: (item) => item.totalFeedbacks },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => navigate(`/uat/sessions/${item.sessionId}/review`)}>
                      Review
                    </Button>
                    {canDeleteSession(item, currentUser, isAdmin) && (
                      <Button
                        variant="link"
                        ariaLabel={`Delete ${item.sessionName}`}
                        onClick={() => setPendingDelete(item)}
                      >
                        Delete
                      </Button>
                    )}
                  </SpaceBetween>
                ),
              },
            ]}
          />
        )}

        <Modal
          visible={pendingDelete !== null}
          // Ignore dismiss while the request is in flight. The DELETE cannot be recalled, so
          // letting the modal close would show the row vanish after the user pressed Cancel.
          onDismiss={() => !isDeleting && setPendingDelete(null)}
          header="Delete UAT session"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={isDeleting} onClick={() => setPendingDelete(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirmDelete} loading={isDeleting}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Delete session <b>{pendingDelete?.sessionName}</b>? This cannot be undone.
        </Modal>

        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          header="Create UAT Session"
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleCreate} loading={isCreating} disabled={!isFormValid}>
                  Create
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Session Name">
              <Input value={sessionName} onChange={({ detail }) => setSessionName(detail.value)} />
            </FormField>
            <LLMTypeSelect
              selectedOption={selectedLLMType}
              onChange={setSelectedLLMType}
              options={uatLlmTypeOptions}
            />
            <PhaseSelect
              selectedOption={selectedPhase}
              onChange={handlePhaseChange}
            />
            <ToolSelectionTable
              phase={selectedPhase?.value ?? null}
              toolsByPhase={toolsByPhase}
              isLoading={isLoadingTools}
              selectedToolIds={selectedToolIds}
              onSelectionChange={setSelectedToolIds}
            />
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default UatSessions;
